import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import { getConferenceTimezone, CURRENT_EVENT_YEAR } from '../schedule.js';
import { dayWindow } from './day-window.js';

dayjs.extend(utc);
dayjs.extend(timezone);

// A gap must be long enough to actually seat a session. Most UNBOUND sessions
// run 30-45 minutes, so 45 is the floor (spec decision 5 — NOT the 60 first
// discussed).
export const MIN_GAP_MINUTES = 45;

const MS_PER_MINUTE = 60 * 1000;

// Merge overlapping/adjacent intervals so subtracting them can't produce a
// phantom gap between a session and one nested inside it.
function mergeIntervals(intervals) {
    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const merged = [];
    for (const cur of sorted) {
        const last = merged[merged.length - 1];
        if (last && cur.start <= last.end) {
            if (cur.end > last.end) last.end = cur.end;
        } else {
            merged.push({ ...cur });
        }
    }
    return merged;
}

// An item "belongs" to `date` (in `tz`) if its start or end falls on that
// calendar date, or if it spans the whole date (starts before and ends after).
// This is independent of the 9-20 agenda window — a 7am breakfast still
// belongs to the day even though it's clamped out of the gap sweep below.
function belongsToDate(item, date, tz) {
    const start = dayjs(item?.start_date_and_time);
    const end = dayjs(item?.end_date_and_time);
    if (!start.isValid() || !end.isValid()) return false;

    const startDate = start.tz(tz).format('YYYY-MM-DD');
    const endDate = end.tz(tz).format('YYYY-MM-DD');
    if (startDate === date || endDate === date) return true;

    // Spans the whole day (starts before `date`, ends after it).
    return startDate < date && endDate > date;
}

/**
 * Open stretches of >= MIN_GAP_MINUTES in `date`'s agenda window, given the
 * user's busy items (scheduled sessions UNION custom events, in the
 * shared/utilities/schedule/overlap.js shape).
 *
 * Interior, leading, and trailing gaps all count. A day with NO busy items
 * AT ALL (regardless of the 9-20 window) returns [] — an empty day keeps its
 * existing day-scoped empty state rather than rendering one 11-hour gap
 * block. A day that has busy items, but all of them fall outside the 9-20
 * window (e.g. an early breakfast), still has "something on it" — the full
 * window comes back as a single gap rather than the empty state.
 *
 * `bounds` (optional) is `{ start, end }` (ISO strings or Dates) — the day's
 * actual catalog programming hours. When given, the gap window is clamped to
 * `[max(9am, start), min(8pm, end)]`, so a trailing gap isn't offered past the
 * last session that could fill it (nor a leading gap before the first one). The
 * calendar grid runs to 8pm even when nothing is scheduled that late, which
 * would otherwise surface a "Find a Session" block over a slot no session could
 * ever fill. Omit it (server-side suggest validation) for the full 9-20 window.
 *
 * Pure: no Vue APIs, no I/O. Imported by BOTH calendar-list.vue (to draw the
 * blocks with no round trip) and the suggest endpoint (to validate the gap it
 * was asked for). One definition of "gap", tested once.
 */
export function findGaps(busyItems, date, tz = getConferenceTimezone(CURRENT_EVENT_YEAR), bounds = null) {
    const { dayStart, dayEnd } = dayWindow(date, tz);
    let windowStart = dayStart.getTime();
    let windowEnd = dayEnd.getTime();

    if (bounds) {
        const boundStart = bounds.start ? new Date(bounds.start).getTime() : NaN;
        const boundEnd = bounds.end ? new Date(bounds.end).getTime() : NaN;
        if (!Number.isNaN(boundStart)) windowStart = Math.max(windowStart, boundStart);
        if (!Number.isNaN(boundEnd)) windowEnd = Math.min(windowEnd, boundEnd);
    }

    const clamped = [];
    let hasItemOnDate = false;
    for (const item of busyItems || []) {
        if (belongsToDate(item, date, tz)) hasItemOnDate = true;

        const start = new Date(item?.start_date_and_time).getTime();
        const end = new Date(item?.end_date_and_time).getTime();
        if (Number.isNaN(start) || Number.isNaN(end)) continue;
        // Drop anything wholly outside the window; clamp anything straddling it.
        if (end <= windowStart || start >= windowEnd) continue;
        clamped.push({ start: Math.max(start, windowStart), end: Math.min(end, windowEnd) });
    }

    // Empty day: no busy items at all, so the existing day-scoped empty
    // state survives. NOTE: this is distinct from `clamped.length === 0` —
    // a day can have items that are all outside the 9-20 window and still
    // be "non-empty" (the sweep below then yields one full-window gap).
    if (!hasItemOnDate) return [];

    const gaps = [];
    let cursor = windowStart;
    for (const busy of mergeIntervals(clamped)) {
        if (busy.start > cursor) gaps.push({ start: cursor, end: busy.start });
        cursor = Math.max(cursor, busy.end);
    }
    if (cursor < windowEnd) gaps.push({ start: cursor, end: windowEnd });

    return gaps
        .map((g) => ({
            startDateTime: new Date(g.start),
            endDateTime: new Date(g.end),
            minutes: Math.round((g.end - g.start) / MS_PER_MINUTE),
        }))
        .filter((g) => g.minutes >= MIN_GAP_MINUTES);
}
